import React from 'react'

import { ActionIcon, CloseButton, Group, Text } from '@mantine/core'
import {
  Dropzone,
  IMAGE_MIME_TYPE,
  PDF_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
} from '@mantine/dropzone'
import { File } from 'lucide-react'

import { toast } from 'src/components/Overlays/Toast/Toast'
import IconUpload from 'src/icons/IconUpload'
import { formatBytes } from 'src/lib/formatters'

const MEGABYTE_TO_BYTE = 1024 ** 2
const DEFAULT_MEGABYTE_LIMIT = 100

interface SelectedFileProps {
  file: File
  onClose: () => void
  showSize?: boolean
}

const SelectedFile = ({
  file,
  onClose,
  showSize = true,
}: SelectedFileProps) => {
  if (!file) return null

  return (
    <div className="flex flex-row items-baseline gap-3 rounded bg-doubleNickel-gray-50 p-2">
      <File size={12} className="stroke-doubleNickel-gray-500" />
      <div className="flex flex-col items-start justify-start">
        <Text size="sm" fw={500}>
          {file.name}
        </Text>
        {showSize && (
          <Text size="xs" fw={200}>
            {formatBytes(file.size)}
          </Text>
        )}
      </div>
      <CloseButton onClick={onClose} size="xs" className="ml-auto" />
    </div>
  )
}

export interface DnUploadProps {
  /** Current selected files */
  files?: File[]
  /** Callback when files are dropped/selected */
  onDrop: (files: File[]) => void
  /** Callback when a file is removed */
  onRemove?: (fileIndex: number) => void
  /** Maximum file size in MB */
  maxSizeMB?: number
  /** Maximum number of files */
  maxFiles?: number
  /** Accepted MIME types */
  accept?: string[]
  /** Whether to show file sizes */
  showFileSize?: boolean
  /** Custom upload text */
  uploadText?: string
  /** Custom file type description */
  fileTypesText?: string
  /** Custom drag and drop text */
  dragText?: string
  /** Custom reject text */
  rejectText?: string
  /** Additional className for styling */
  className?: string
  /** Whether to show selected files */
  showSelectedFiles?: boolean
}

const DnUpload = ({
  files = [],
  onDrop,
  onRemove,
  maxSizeMB = DEFAULT_MEGABYTE_LIMIT,
  maxFiles = 1,
  accept = [...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE, ...MS_WORD_MIME_TYPE, ...MS_EXCEL_MIME_TYPE],
  showFileSize = true,
  uploadText = 'Click to upload',
  fileTypesText = `PDF, PNG, JPG, JPEG, XLS or XLSX (Max ${DEFAULT_MEGABYTE_LIMIT}MB)`,
  dragText = 'Drag and drop the file here',
  rejectText = 'File does not meet requirements',
  className = '',
  showSelectedFiles = true,
}: DnUploadProps) => {
  const maxSizeBytes = maxSizeMB * MEGABYTE_TO_BYTE

  const handleDrop = (droppedFiles: File[]) => {
    onDrop(droppedFiles)
  }

  const handleReject = (fileRejections) => {
    fileRejections.forEach((rejection) => {
      if (rejection.errors[0].code === 'file-too-large') {
        toast(`File size exceeds the maximum limit of ${maxSizeMB}MB`, 'error')
      } else {
        toast(`Unknown error occurred: ${rejection.errors[0].code}`, 'error')
      }
    })
  }

  const handleRemoveFile = (fileIndex: number) => {
    if (onRemove) {
      onRemove(fileIndex)
    }
  }

  const shouldShowDropzone = files.length === 0 || maxFiles > 1

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {shouldShowDropzone && (
        <div className="flex flex-col py-4">
          <Dropzone
            accept={accept}
            maxFiles={maxFiles}
            maxSize={maxSizeBytes}
            multiple={maxFiles > 1} // Restrict browser file dialog to single file when maxFiles=1
            onDrop={handleDrop}
            onReject={handleReject}
          >
            <Group className="flex flex-col">
              <Dropzone.Accept>
                <p>{dragText}</p>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <p>{rejectText}</p>
              </Dropzone.Reject>
              <Dropzone.Idle>
                <ActionIcon
                  variant="subtle"
                  className="h-10 w-10 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm"
                >
                  <IconUpload className="fill-none stroke-doubleNickel-gray-700" />
                </ActionIcon>
              </Dropzone.Idle>
              <div className="flex flex-col items-center gap-1">
                <Text className="text-sm text-doubleNickel-gray-600">
                  <span className="font-semibold text-doubleNickel-brand-500">
                    {uploadText + ' '}
                  </span>
                  or drag and drop
                </Text>
                <Text size="sm" className="text-xs text-doubleNickel-gray-600">
                  {fileTypesText.replace(
                    `${DEFAULT_MEGABYTE_LIMIT}MB`,
                    `${maxSizeMB}MB`
                  )}
                </Text>
              </div>
            </Group>
          </Dropzone>
        </div>
      )}

      {showSelectedFiles && files.length > 0 && (
        <div className="flex flex-col gap-3">
          {files.map((file, index) => (
            <SelectedFile
              key={`${file.name}-${index}`}
              file={file}
              onClose={() => handleRemoveFile(index)}
              showSize={showFileSize}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default DnUpload
